import {
  faBatteryQuarter,
  faBoltLightning,
  faClockRotateLeft,
  faGaugeHigh,
  faGem,
  faPlug,
  faTriangleExclamation,
  faTruckFront,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AdvancedAssignmentFilterOption, CaseModel } from 'api/model';
import styled from 'styled-components';

export type VehicleClassTone = 'risk' | 'warn' | 'super' | 'premium' | 'neutral';

type Definition = {
  caseFlag: keyof CaseModel;
  filterOption: AdvancedAssignmentFilterOption;
  label: string;
  /** Tooltip, när etiketten inte säger allt. */
  hint?: string;
  icon: typeof faGem;
  tone: VehicleClassTone;
  severity: number;
};

export const VEHICLE_CLASS_DEFINITIONS: Definition[] = [
  {
    caseFlag: 'isRiskVehicle',
    filterOption: AdvancedAssignmentFilterOption.VehicleIsRiskVehicle,
    label: 'Riskbil',
    icon: faTriangleExclamation,
    tone: 'risk',
    severity: 7,
  },
  {
    caseFlag: 'isSupercar',
    filterOption: AdvancedAssignmentFilterOption.VehicleIsSupercar,
    label: 'Superbil',
    icon: faBoltLightning,
    tone: 'super',
    severity: 6,
  },
  {
    caseFlag: 'isPremiumCar',
    filterOption: AdvancedAssignmentFilterOption.VehicleIsPremiumCar,
    label: 'Premium',
    icon: faGem,
    tone: 'premium',
    severity: 5,
  },
  {
    caseFlag: 'isHighMileage',
    filterOption: AdvancedAssignmentFilterOption.VehicleIsHighMileage,
    label: 'Högmilad',
    icon: faGaugeHigh,
    tone: 'warn',
    severity: 4,
  },
  {
    caseFlag: 'isOlderModel',
    filterOption: AdvancedAssignmentFilterOption.VehicleIsOlderModel,
    label: 'Äldre modell',
    icon: faClockRotateLeft,
    tone: 'warn',
    severity: 3,
  },
  {
    caseFlag: 'isTransportVehicle',
    filterOption: AdvancedAssignmentFilterOption.VehicleIsTransportVehicle,
    label: 'Transportbil',
    icon: faTruckFront,
    tone: 'neutral',
    severity: 2,
  },
  {
    caseFlag: 'isLowRangeElectric',
    filterOption: AdvancedAssignmentFilterOption.VehicleIsLowRangeElectric,
    label: 'Dålig räckvidd',
    hint: 'Elbil med dålig räckvidd – rekommenderas lastbil',
    icon: faBatteryQuarter,
    tone: 'warn',
    severity: 1.5,
  },
  {
    caseFlag: 'isElectric',
    filterOption: AdvancedAssignmentFilterOption.VehicleIsElectric,
    label: 'Elbil',
    icon: faPlug,
    tone: 'neutral',
    severity: 1,
  },
];

export const VEHICLE_CLASS_FILTER_OPTIONS: ReadonlySet<AdvancedAssignmentFilterOption> =
  new Set(VEHICLE_CLASS_DEFINITIONS.map((d) => d.filterOption));

const VEHICLE_CLASS_BY_FILTER_OPTION: Record<number, Definition> =
  Object.fromEntries(
    VEHICLE_CLASS_DEFINITIONS.map((d) => [d.filterOption, d])
  );

export const getVehicleClassByFilterOption = (
  filterOption: AdvancedAssignmentFilterOption
): Definition | undefined => VEHICLE_CLASS_BY_FILTER_OPTION[filterOption];

export const TONE_BG: Record<VehicleClassTone, string> = {
  risk: '#fee2e2',
  super: '#d1fae5',
  warn: '#fef3c7',
  premium: '#dbeafe',
  neutral: '#e5e7eb',
};

export const TONE_FG: Record<VehicleClassTone, string> = {
  risk: '#991b1b',
  super: '#065f46',
  warn: '#854d0e',
  premium: '#1e40af',
  neutral: '#374151',
};

export const Chip = styled.span<{ $tone: VehicleClassTone; $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? '0' : '4px')};
  padding: ${({ $compact }) => ($compact ? '2px 5px' : '1px 8px')};
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  line-height: 1.5;
  white-space: nowrap;
  background: ${({ $tone }) => TONE_BG[$tone]};
  color: ${({ $tone }) => TONE_FG[$tone]};
`;

export const ChipIcon = styled(FontAwesomeIcon)<{ $compact?: boolean }>`
  font-size: ${({ $compact }) => ($compact ? '11px' : '10px')};
`;

// "Dålig räckvidd" säger redan att det är en elbil, så Elbil-chipet döljs bredvid det.
export const activeVehicleClassFlags = (c: CaseModel): Definition[] =>
  VEHICLE_CLASS_DEFINITIONS.filter(
    (d) =>
      c[d.caseFlag] && !(d.caseFlag === 'isElectric' && c.isLowRangeElectric)
  ).sort((a, b) => b.severity - a.severity);
